import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const KeyboardControlsContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: rgba(26, 26, 26, 0.7);
  border-radius: 10px;
  padding: 0.8rem;
  color: var(--color-white);
  font-size: 0.9rem;
  border: 1px solid rgba(255, 144, 82, 0.2);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    bottom: 15px;
    right: 15px;
    padding: 1rem;
    font-size: 1rem;
  }
`;

const ControlsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem 0;
  
  @media (max-width: 768px) {
    padding: 0.7rem 0;
  }
`;

const ControlsTitle = styled.h3`
  font-size: 1rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ControlsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.8rem;
    margin-top: 0.8rem;
  }
`;

const ControlItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const KeyGroup = styled.div`
  display: flex;
  gap: 0.2rem;
`;

const KeyButton = styled.span`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 144, 82, 0.3);
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
  font-size: 0.8rem;
  min-width: 24px;
  text-align: center;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.5rem;
    font-size: 0.9rem;
    min-width: 30px;
  }
`;

const KeyDescription = styled.span`
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// Componente de controles de toque para dispositivos móveis
const TouchControls = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 144, 82, 0.2);
  }
`;

const TouchControlTitle = styled.h4`
  font-size: 1rem;
  margin: 0;
  color: var(--color-primary);
`;

const TouchControlItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const GestureIcon = styled.span`
  font-size: 1.2rem;
`;

const KeyboardControls: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [activeKeys, setActiveKeys] = useState<{ [key: string]: boolean }>({});
  
  // Detectar se é um dispositivo móvel
  const [isMobile, setIsMobile] = useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      setActiveKeys(prev => ({ ...prev, [key]: true }));
      
      // Implementar navegação por teclado
      switch (key) {
        case 'w':
          window.scrollBy(0, -100);
          break;
        case 's':
          window.scrollBy(0, 100);
          break;
        case 'a':
          // Navegar para a seção anterior
          break;
        case 'd':
          // Navegar para a próxima seção
          break;
        case 'e':
          // Interagir com elemento em foco
          break;
        case 'q':
          // Voltar ao menu
          break;
        case 'escape':
          // Abrir menu de pausa
          break;
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      setActiveKeys(prev => ({ ...prev, [key]: false }));
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  
  // Implementar detecção de gestos para dispositivos móveis
  React.useEffect(() => {
    if (!isMobile) return;
    
    let touchStartY = 0;
    let touchStartX = 0;
    
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!e.touches[0]) return;
      
      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      
      // Detectar swipe vertical
      if (Math.abs(touchY - touchStartY) > 50) {
        if (touchY < touchStartY) {
          // Swipe para cima
          window.scrollBy(0, -50);
        } else {
          // Swipe para baixo
          window.scrollBy(0, 50);
        }
        
        // Atualizar posição inicial para evitar scrolling contínuo
        touchStartY = touchY;
      }
      
      // Detectar swipe horizontal
      if (Math.abs(touchX - touchStartX) > 100) {
        if (touchX < touchStartX) {
          // Swipe para a esquerda (próxima seção)
          // Implementar navegação
        } else {
          // Swipe para a direita (seção anterior)
          // Implementar navegação
        }
        
        // Atualizar posição inicial
        touchStartX = touchX;
      }
    };
    
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isMobile]);
  
  const toggleExpanded = () => {
    setExpanded(!expanded);
    console.log("Toggle clicked, expanded state:", !expanded);
  };

  // Botão de colapso simplificado (apenas ícone)
  const CollapseButton = styled.button`
    font-size: 1.2rem;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 5px;
    
    @media (max-width: 768px) {
      font-size: 1.4rem;
      padding: 8px;
    }
  `;

  return (
    <KeyboardControlsContainer
      as={motion.div}
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2, duration: 0.5 }}
    >
      <ControlsHeader onClick={toggleExpanded} style={{ cursor: 'pointer' }}>
        <ControlsTitle>
          {isMobile ? (
            <>
              <span>↑</span>
              Touch Controls
            </>
          ) : (
            <>
              <span>⌨</span>
              Keyboard Controls
            </>
          )}
        </ControlsTitle>
        <CollapseButton>
          {expanded ? '▼' : '▲'}
        </CollapseButton>
      </ControlsHeader>
      
      {expanded && !isMobile && (
        <ControlsList>
          <ControlItem>
            <KeyGroup>
              <KeyButton style={{ background: activeKeys['w'] ? 'rgba(255, 144, 82, 0.3)' : '' }}>W</KeyButton>
            </KeyGroup>
            <KeyDescription>Scroll Up</KeyDescription>
          </ControlItem>
          
          <ControlItem>
            <KeyGroup>
              <KeyButton style={{ background: activeKeys['a'] ? 'rgba(255, 144, 82, 0.3)' : '' }}>A</KeyButton>
            </KeyGroup>
            <KeyDescription>Previous Section</KeyDescription>
          </ControlItem>
          
          <ControlItem>
            <KeyGroup>
              <KeyButton style={{ background: activeKeys['s'] ? 'rgba(255, 144, 82, 0.3)' : '' }}>S</KeyButton>
            </KeyGroup>
            <KeyDescription>Scroll Down</KeyDescription>
          </ControlItem>
          
          <ControlItem>
            <KeyGroup>
              <KeyButton style={{ background: activeKeys['d'] ? 'rgba(255, 144, 82, 0.3)' : '' }}>D</KeyButton>
            </KeyGroup>
            <KeyDescription>Next Section</KeyDescription>
          </ControlItem>
          
          <ControlItem>
            <KeyGroup>
              <KeyButton style={{ background: activeKeys['e'] ? 'rgba(255, 144, 82, 0.3)' : '' }}>E</KeyButton>
            </KeyGroup>
            <KeyDescription>Interact</KeyDescription>
          </ControlItem>
          
          <ControlItem>
            <KeyGroup>
              <KeyButton style={{ background: activeKeys['q'] ? 'rgba(255, 144, 82, 0.3)' : '' }}>Q</KeyButton>
            </KeyGroup>
            <KeyDescription>Back to Menu</KeyDescription>
          </ControlItem>
          
          <ControlItem>
            <KeyGroup>
              <KeyButton style={{ background: activeKeys['escape'] ? 'rgba(255, 144, 82, 0.3)' : '' }}>ESC</KeyButton>
            </KeyGroup>
            <KeyDescription>Pause Menu</KeyDescription>
          </ControlItem>
        </ControlsList>
      )}
      
      {expanded && isMobile && (
        <TouchControls>
          <TouchControlTitle>Available Gestures:</TouchControlTitle>

          <TouchControlItem>
            <GestureIcon>↑</GestureIcon>
            <KeyDescription>Swipe up to scroll the page</KeyDescription>
          </TouchControlItem>
          
          <TouchControlItem>
            <GestureIcon>↓</GestureIcon>
            <KeyDescription>Swipe down to scroll the page</KeyDescription>
          </TouchControlItem>
          
          <TouchControlItem>
            <GestureIcon>←</GestureIcon>
            <KeyDescription>Swipe left to go to the next section</KeyDescription>
          </TouchControlItem>
          
          <TouchControlItem>
            <GestureIcon>→</GestureIcon>
            <KeyDescription>Swipe right to go to the previous section</KeyDescription>
          </TouchControlItem>
          
          <TouchControlItem>
            <GestureIcon>•</GestureIcon>
            <KeyDescription>Tap on items to interact</KeyDescription>
          </TouchControlItem>
          
          <TouchControlItem>
            <GestureIcon>••</GestureIcon>
            <KeyDescription>Double tap to go back to Menu</KeyDescription>
          </TouchControlItem>
        </TouchControls>
      )}
    </KeyboardControlsContainer>
  );
};

export default KeyboardControls;
